import React from 'react';
import { ArrowRight, TrendingUp, Shield, Users, CheckCircle, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import { companyInfo, services, testimonials } from '../mock/data';
import SEO from '../components/SEO';

const HomePage = () => {
  return (
    <div className="min-h-screen">
      <SEO
        title="Equi Pulse | SEBI Registered Research Analyst | Stock Market Research India"
        description="Equi Pulse is a SEBI registered research analyst offering expert stock market research, equity analysis, and investment insights for Indian markets."
        url=""
        type="website"
      />
      {/* Hero Section with Side-by-Side Layout */}
      <section className="relative min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/30 to-slate-950 pt-20 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="container mx-auto px-4 py-12 lg:py-20 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center min-h-[calc(100vh-120px)]">
            {/* Left Side - Text Content */}
            <div className="order-2 lg:order-1 text-center lg:text-left">
              <div className="inline-block mb-6 px-6 py-2 bg-emerald-900/30 backdrop-blur-sm border border-amber-500/30 rounded-full">
                <p className="text-amber-400 text-sm font-semibold">
                  SEBI Registered Research Analyst | {companyInfo.experience} Years Experience
                </p>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-tight">
                <span className="text-amber-400">
                  SEBI Registered Research Analyst
                </span>
                <br />
                <span className="text-white text-3xl sm:text-4xl lg:text-5xl">
                  Expert Stock Market Research
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-3">
                Stay ahead of the curve with insights that matter,
              </p>
              <p className="text-lg sm:text-xl lg:text-2xl text-gray-300 mb-8 lg:mb-10">
                tools that deliver, and experts who understand your goals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/services"
                  className="group px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-2xl shadow-emerald-900/50 transition-all duration-300 flex items-center justify-center gap-2"
                >
                  Explore Our Services
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
                </Link>
                <Link
                  to="/contact"
                  className="px-8 py-4 bg-slate-800/50 backdrop-blur-sm border border-amber-500/30 hover:bg-slate-800 hover:border-amber-500/50 text-white font-semibold rounded-lg transition-all duration-300"
                >
                  Book Free Consultation
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="mt-10 lg:mt-12 flex flex-wrap gap-6 justify-center lg:justify-start">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-500" aria-hidden="true" />
                  <span className="text-gray-400 text-sm">SEBI Registered</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-emerald-500" aria-hidden="true" />
                  <span className="text-gray-400 text-sm">500+ Happy Clients</span>
                </div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-500" aria-hidden="true" />
                  <span className="text-gray-400 text-sm">Proven Track Record</span>
                </div>
              </div>
            </div>

            {/* Right Side - Video */}
            <div className="order-1 lg:order-2 flex justify-center lg:justify-end">
              <div className="relative w-full max-w-xl lg:max-w-none">
                {/* Video Container with Decorative Frame */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-emerald-900/30 border border-amber-500/20">
                  {/* Decorative corner accents */}
                  <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-amber-500/50 rounded-tl-2xl z-10" />
                  <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-amber-500/50 rounded-tr-2xl z-10" />
                  <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-amber-500/50 rounded-bl-2xl z-10" />
                  <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-amber-500/50 rounded-br-2xl z-10" />
                  
                  {/* YouTube Video Embed */}
                  <div className="relative aspect-video bg-slate-900">
                    <iframe
                      src="https://www.youtube.com/embed/Hj5YdpdF4VQ?autoplay=1&mute=1&loop=1&playlist=Hj5YdpdF4VQ&controls=0&showinfo=0&modestbranding=1&rel=0&playsinline=1"
                      title="Equi Pulse Introduction"
                      className="absolute inset-0 w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      frameBorder="0"
                    />
                  </div>
                </div>

                {/* Floating Badge */}
                <div className="absolute -bottom-4 -left-4 lg:-left-8 bg-gradient-to-r from-emerald-700 to-emerald-600 px-4 py-2 rounded-lg shadow-lg flex items-center gap-2 z-20">
                  <Play className="w-4 h-4 text-white" aria-hidden="true" />
                  <span className="text-white text-sm font-semibold">Watch Intro</span>
                </div>

                {/* Background Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-emerald-500/20 to-amber-500/20 rounded-3xl blur-2xl -z-10" />
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce hidden lg:block">
          <div className="w-6 h-10 border-2 border-amber-500/50 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-amber-500 rounded-full mt-2" />
          </div>
        </div>
      </section>

      {/* Trading Services Quick Preview */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300">
              <h3 className="text-xl font-bold text-amber-400 mb-2">Single Day Trading</h3>
              <p className="text-gray-400 text-sm mb-4">Real-Time Strategies for Fast-Moving Markets</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  Short to Mid-Term Trades
                </li>
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  Technical & Fundamental Analysis
                </li>
              </ul>
            </div>

            <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300">
              <h3 className="text-xl font-bold text-amber-400 mb-2">Swing Trading</h3>
              <p className="text-gray-400 text-sm mb-4">Opportunities for Strategic Growth</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  Data-Driven Analysis
                </li>
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  Risk-Optimized Strategies
                </li>
              </ul>
            </div>

            <div className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300">
              <h3 className="text-xl font-bold text-amber-400 mb-2">Research & Reports</h3>
              <p className="text-gray-400 text-sm mb-4">Stay Ahead with Market Intelligence</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  In-Depth Market Analysis
                </li>
                <li className="flex items-start gap-2 text-gray-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  Actionable Insights
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block mb-4 px-4 py-1.5 bg-emerald-900/30 border border-amber-500/30 rounded-full">
                <p className="text-amber-400 text-sm font-semibold">Where Passion Meets Performance</p>
              </div>
              <h2 className="text-4xl font-bold text-white mb-6">
                <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
                  {companyInfo.experience} Years
                </span>{' '}
                Proven Experience
              </h2>
              <p className="text-gray-300 mb-6 leading-relaxed">
                We are a SEBI Registered Research Analyst. Our team has over {companyInfo.experience} years of collective experience in equity research and market strategy. Our expertise spans technical and fundamental analysis, financial modeling, valuation, and risk–return optimization.
              </p>
              <p className="text-gray-300 mb-8 leading-relaxed">
                We are committed to providing data-driven, ethical, and client-centric research solutions that help traders, investors, and institutions navigate dynamic market conditions with confidence and discipline.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300"
              >
                Learn more about Equi Pulse
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </div>

            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1593630363221-fd977445c8e7?auto=format&fit=crop&w=800&h=600&q=75"
                  alt="Stock Market Financial Analysis Chart by Equi Pulse Research"
                  width="800"
                  height="600"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent" />
              </div>

              {/* Stats Overlay */}
              <div className="absolute -bottom-6 -left-6 right-6 grid grid-cols-3 gap-4">
                <div className="p-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg border border-emerald-700/30 backdrop-blur-sm">
                  <TrendingUp className="w-6 h-6 text-emerald-500 mb-2" aria-hidden="true" />
                  <p className="text-2xl font-bold text-white">{companyInfo.experience}</p>
                  <p className="text-xs text-gray-400">Years Experience</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg border border-emerald-700/30 backdrop-blur-sm">
                  <Shield className="w-6 h-6 text-amber-500 mb-2" aria-hidden="true" />
                  <p className="text-2xl font-bold text-white">SEBI</p>
                  <p className="text-xs text-gray-400">Registered</p>
                </div>
                <div className="p-4 bg-gradient-to-br from-slate-900 to-slate-800 rounded-lg border border-emerald-700/30 backdrop-blur-sm">
                  <Users className="w-6 h-6 text-emerald-500 mb-2" aria-hidden="true" />
                  <p className="text-2xl font-bold text-white">500+</p>
                  <p className="text-xs text-gray-400">Clients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Our <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Financial insights and strategies to guide your investments confidently.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className={`relative p-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border ${
                  service.popular ? 'border-amber-500/50 shadow-2xl shadow-amber-900/20' : 'border-emerald-700/30'
                } hover:border-amber-500/50 transition-all duration-300 group`}
              >
                {service.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-6 py-1.5 bg-gradient-to-r from-amber-600 to-amber-500 text-white text-sm font-bold rounded-full shadow-lg">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-amber-400 mb-3">{service.name}</h3>
                <p className="text-gray-400 text-sm mb-6">{service.tagline}</p>
                
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 text-emerald-400 hover:text-amber-400 font-semibold transition-colors duration-200 group"
                >
                  Learn more about {service.name}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" aria-hidden="true" />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-900/50 transition-all duration-300"
            >
              View All Services
              <ArrowRight className="w-5 h-5" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              What Our <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">Clients Say</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border border-emerald-700/30 hover:border-amber-500/50 transition-all duration-300"
              >
                <div className="flex gap-1 mb-4" role="img" aria-label={`${testimonial.rating} out of 5 stars`}>
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-amber-500 text-lg" aria-hidden="true">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 italic">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-emerald-700/50"
                  />
                  <div>
                    <p className="text-white font-semibold">{testimonial.name}</p>
                    <p className="text-gray-400 text-sm">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-950 via-slate-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?auto=format&fit=crop&w=1920')] bg-cover bg-center opacity-5" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Make Smarter{' '}
              <span className="bg-gradient-to-r from-emerald-400 to-amber-400 bg-clip-text text-transparent">
                Investment Decisions?
              </span>
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Join thousands of investors who trust Equi Pulse for market insights and proven strategies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="px-8 py-4 bg-gradient-to-r from-emerald-700 to-emerald-600 hover:from-emerald-600 hover:to-emerald-500 text-white font-semibold rounded-lg shadow-2xl shadow-emerald-900/50 transition-all duration-300"
              >
                Get Started Today
              </Link>
              <Link
                to="/services"
                className="px-8 py-4 bg-slate-800/50 backdrop-blur-sm border border-amber-500/30 hover:bg-slate-800 text-white font-semibold rounded-lg transition-all duration-300"
              >
                Explore Services
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
